import { useCallback, useEffect, useRef, useState } from 'react';
import { singletonHook } from 'react-singleton-hook';
import {
  NavigationInterceptionCapability,
  Specs,
} from '@doxyme/extensions-bridge';
import { useAddCapability } from '@doxyme/extensions-bridge-react';
import { bridge } from '../../../features/extensions/instances';
import { NavigationConfirmationAlertProps } from '../../../components/navigationConfirmationAlert';

export type NavigationConfirmationReason = {
  id: string;
  title: string;
  description: string;
  stayButtonText: string;
  leaveButtonText: string;
} & Pick<
  NavigationConfirmationAlertProps,
  'statusIcon' | 'allowClientSideNavigation'
>;

type NavigationAddPayload = Specs.NavigationInterception.NavigationAddPayload;
type NavigationRemovePayload =
  Specs.NavigationInterception.NavigationRemovePayload;
type NavigationManualTriggerPayload =
  Specs.NavigationInterception.NavigationManualTriggerPayload;

export type UseExtensionsNavigationInterceptionReturnType = {
  navigationConfirmationReasons: NavigationConfirmationReason[];
  manualTriggeredReason: NavigationConfirmationReason | undefined;
  addNavigationConfirmationReason: (
    reason: NavigationConfirmationReason,
  ) => void;
  removeNavigationConfirmationReason: (payload: { id: string }) => void;
  clearNavigationConfirmationReasons: () => void;
  resolveManualTrigger: (
    decision: Specs.NavigationInterception.NavigationDecision,
  ) => void;
};

const hookInitValues: UseExtensionsNavigationInterceptionReturnType = {
  navigationConfirmationReasons: [],
  manualTriggeredReason: undefined,
  addNavigationConfirmationReason: () => void 0,
  removeNavigationConfirmationReason: () => void 0,
  clearNavigationConfirmationReasons: () => void 0,
  resolveManualTrigger: () => void 0,
};

export const useExtensionsNavigationInterceptionImpl =
  (): UseExtensionsNavigationInterceptionReturnType => {
    const [navigationConfirmationReasons, setNavigationConfirmationReasons] =
      useState<NavigationConfirmationReason[]>([]);
    const [manualTriggeredReason, setManualTriggeredReason] = useState<
      NavigationConfirmationReason | undefined
    >();

    const addNavigationConfirmationReason = (
      reason: NavigationConfirmationReason,
    ) => {
      setNavigationConfirmationReasons((prev) => {
        const idx = prev.findIndex((r) => r.id === reason.id);
        if (idx !== -1) {
          const updated = [...prev];
          updated[idx] = reason;
          return updated;
        }
        return [...prev, reason];
      });
    };

    const removeNavigationConfirmationReason = (payload: { id: string }) => {
      setNavigationConfirmationReasons((prev) =>
        prev.filter((r) => r.id !== payload.id),
      );
    };

    const clearNavigationConfirmationReasons = () => {
      setNavigationConfirmationReasons([]);
    };

    const navigationInterception = useAddCapability(
      bridge,
      () => new NavigationInterceptionCapability(),
    );

    const capabilityRef = useRef(navigationInterception);
    capabilityRef.current = navigationInterception;

    const reasonsRef = useRef(navigationConfirmationReasons);
    reasonsRef.current = navigationConfirmationReasons;

    useEffect(() => {
      const unsubscribeBlock = navigationInterception.onAdd(
        (payload: NavigationAddPayload): void => {
          addNavigationConfirmationReason(
            payload as NavigationConfirmationReason,
          );
        },
      );

      const unsubscribeAllow = navigationInterception.onRemove(
        (payload: NavigationRemovePayload): void => {
          removeNavigationConfirmationReason(payload);
        },
      );

      const unsubscribeTrigger = navigationInterception.onManualTrigger(
        (payload: NavigationManualTriggerPayload): void => {
          const reason = reasonsRef.current.find((r) => r.id === payload.id);
          if (reason) {
            setManualTriggeredReason(reason);
          } else {
            capabilityRef.current.resolveManualTrigger(payload.id, 'stay');
          }
        },
      );

      return () => {
        unsubscribeBlock();
        unsubscribeAllow();
        unsubscribeTrigger();
      };
    }, []);

    const resolveManualTrigger = useCallback(
      (decision: Specs.NavigationInterception.NavigationDecision) => {
        if (manualTriggeredReason) {
          capabilityRef.current.resolveManualTrigger(
            manualTriggeredReason.id,
            decision,
          );
          setManualTriggeredReason(undefined);
        }
      },
      [manualTriggeredReason],
    );

    return {
      navigationConfirmationReasons,
      manualTriggeredReason,
      addNavigationConfirmationReason,
      removeNavigationConfirmationReason,
      clearNavigationConfirmationReasons,
      resolveManualTrigger,
    };
  };

export const useExtensionsNavigationInterception = singletonHook(
  hookInitValues,
  useExtensionsNavigationInterceptionImpl,
);
